<template>
  <div class="!left-auto !right-0 z-40 h-max w-fit">
    <Button
      v-if="!wishlistRemoveCta"
      type="ghost"
      native-type="submit"
      data-qa="add-to-wishlist"
      class="cursor-pointer !p-3"
      :aria-label="wishlistLabel"
      @click="toggleItemInWishlist"
    >
      <component
        :is="isInWishlist ? 'IconHeartFull' : 'IconHeart'"
        class="size-8"
      />
    </Button>
    <Button
      v-else-if="wishlistRemoveCta"
      type="ghost"
      native-type="submit"
      class="cursor-pointer p-3"
      :aria-label="$t('pdp.remove_from_wishlist')"
      @click="removeItemFromWishlist"
    >
      <IconCloseS
        class="size-8"
        data-test-id="remove-item-from-wishlist-button"
        icon="close-s"
      />
    </Button>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { Product } from '@scayle/storefront-nuxt'
import type { ListItemType } from '~/types/tracking'

const props = defineProps<{
  product: Product
  productName: string
  wishlistRemoveCta?: boolean
  listType?: ListItemType
  listName?: string
  listIndex?: number
}>()

const { id: productId } = props.product
const { t } = useI18n()
const { trackRemoveFromWishlist, trackAddToWishlist } = useTrackingEvents()

const { findItem, toggleItem } = useAgsWishlist()

const toggleItemInWishlist = async () => {
  const wasInWishlist = !!findItem({ productId })

  const toggled = await safeToggleWishlistItem(toggleItem, { productId })
  if (!toggled) {
    return
  }

  if (wasInWishlist) {
    trackRemoveFromWishlist({
      product: props.product,
    })
  } else {
    trackAddToWishlist({
      product: props.product,
      index: props.listIndex,
      listName: props.listName,
      listType: props.listType,
      type: 'item_list',
    })
  }
}

const removeItemFromWishlist = async () => {
  const entry = findItem({ productId: props.product.id })
  if (!entry) {
    return
  }
  const toggled = await safeToggleWishlistItem(toggleItem, { productId })
  if (!toggled) {
    return
  }
  trackRemoveFromWishlist({
    product: props.product,
    variant: entry.variant || entry.product?.variants![0],
  })
}

const isInWishlist = computed(() => !!findItem({ productId }))

const wishlistLabel = computed(() => {
  return isInWishlist.value
    ? t('pdp.remove_from_wishlist')
    : t('pdp.add_to_wishlist')
})
</script>
